import { component, computed, observable, subscribe } from 'knockout-decorators'

import { CartField } from '@tixa/schema'
import type { Organization, Person } from '@tixa/schema'
import { Analytics, Authentication, Storage, Language } from '~/utils'

import source from './template.html?raw'
import { CartState } from '../state'
import { authState } from '~/components/login/utils/state'

import { countries } from '~/common/data/countries'
import { getCityFromHungarianPostalCode } from '~/utils/postalCodes'

import './styles.css'
import { CountryCode } from '@tixa/schema'

@component('cart-form', source)
export class CartForm {
  private Locale = Language.getInstance()

  @observable public availableCountries: CountryCode[] = []

  @observable public hasInvoice = true
  @observable public isSubmitDisabled = false
  @observable public isInvoiceDifferent = false

  @observable public email = ''
  @observable public emailConfirmation = ''
  @observable public name = ''
  @observable public zip = ''
  @observable public city = ''
  @observable public address = ''
  @observable public phone = ''
  @observable public selectedCountry = CartState.DEFAULT_COUNTRY

  @observable public emailFeedback = ''
  @observable public emailConfirmationFeedback = ''

  @observable public invoiceName = ''
  @observable public invoiceZip = ''
  @observable public invoiceCity = ''
  @observable public invoiceAddress = ''
  @observable public invoiceVat = ''

  @observable public pass1 = ''
  @observable public pass2 = ''
  @observable public message = ''
  @observable public checkboxValues: { [id: string]: boolean } = {}

  @observable public cityAutoFilled = false
  @observable public invoiceCityAutoFilled = false

  private cartState: CartState = CartState.get()

  @observable public optionalFields: CartField[] =
    this.cartState.content?.fields || []

  private Tracker = Analytics.getInstance()

  private formEl: HTMLElement | null = null

  constructor() {
    if (this.cartState.content) {
      this.loadPerson(this.cartState.content.person)
      this.loadInvoice(this.cartState.content.invoice)
      this.initializeCheckboxTracking()

      if (this.cartState.content.password) {
        this.pass1 = this.pass2 = this.cartState.content.password
      }
      if (this.cartState.content.message) {
        this.message = this.cartState.content.message
      }
    }

    if (Authentication.getInstance().LoggedIn()) {
      this.initLoggedIn()
    } else {
      this.initLoggedOut()
    }

    this.initializeCountries()

    this.cartState.person = this.getPerson
    this.cartState.organization = this.getInvoice

    subscribe<Person>(
      () => this.getPerson,
      (newPerson) => (this.cartState.person = newPerson)
    )
    subscribe<Organization>(
      () => this.getInvoice,
      (newOrg) => (this.cartState.organization = newOrg)
    )
    subscribe<string>(
      () => this.pass1,
      (newValue) => {
        if (this.cartState.content) {
          this.cartState.content.password = newValue
        }
      }
    )
    subscribe<string>(
      () => this.message,
      (newValue) => {
        if (this.cartState.content) {
          this.cartState.content.message = newValue
        }
      }
    )
    subscribe<boolean>(
      () => this.invoiceIndividualsOnly,
      (newValue) => {
        if (newValue) {
          this.isInvoiceDifferent = false
        }
      }
    )
    subscribe<CartField[]>(
      () => this.checkboxFields,
      () => {
        this.initializeCheckboxTracking()
      }
    )

    subscribe<boolean>(
      () => this.isInvoiceDifferent,
      (newValue) => {
        this.cartState.isInvoiceDifferent = newValue
      }
    )

    subscribe<CountryCode>(
      () => this.selectedCountry,
      (newCountry) => {
        this.cartState.selectedCountryId =
          newCountry?.id || CartState.DEFAULT_COUNTRY_ID
      }
    )

    // Keep selectedCountry in sync with selectedCountryId
    subscribe<number>(
      () => this.cartState.selectedCountryId,
      (newCountryId) => {
        if (this.selectedCountry?.id !== newCountryId) {
          this.selectedCountry =
            this.availableCountries.find(
              (country) => country.id === newCountryId
            ) || CartState.DEFAULT_COUNTRY
        }
      }
    )

    subscribe<string>(
      () => this.zip,
      (newZip) => {
        if (
          this.selectedCountry?.id === CartState.DEFAULT_COUNTRY_ID &&
          newZip.length === 4
        ) {
          const city = getCityFromHungarianPostalCode(newZip)
          if (city) {
            this.city = city
            this.cityAutoFilled = true
          }
        } else if (
          this.selectedCountry?.id === CartState.DEFAULT_COUNTRY_ID &&
          newZip.length < 4
        ) {
          if (this.cityAutoFilled) {
            this.city = ''
            this.cityAutoFilled = false
          }
        }
      }
    )

    subscribe<string>(
      () => this.invoiceZip,
      (newInvoiceZip) => {
        if (
          this.selectedCountry?.id === CartState.DEFAULT_COUNTRY_ID &&
          newInvoiceZip.length === 4
        ) {
          const city = getCityFromHungarianPostalCode(newInvoiceZip)
          if (city) {
            this.invoiceCity = city
            this.invoiceCityAutoFilled = true
          }
        } else if (
          this.selectedCountry?.id === CartState.DEFAULT_COUNTRY_ID &&
          newInvoiceZip.length < 4
        ) {
          if (this.invoiceCityAutoFilled) {
            this.invoiceCity = ''
            this.invoiceCityAutoFilled = false
          }
        }
      }
    )

    subscribe<string>(
      () => this.city,
      (newCity) => {
        if (
          this.cityAutoFilled &&
          newCity !== getCityFromHungarianPostalCode(this.zip)
        ) {
          this.cityAutoFilled = false
        }
      }
    )

    subscribe<string>(
      () => this.invoiceCity,
      (newInvoiceCity) => {
        if (
          this.invoiceCityAutoFilled &&
          newInvoiceCity !== getCityFromHungarianPostalCode(this.invoiceZip)
        ) {
          this.invoiceCityAutoFilled = false
        }
      }
    )

    this.initializeMutationObserver()
  }

  private async initLoggedIn() {
    try {
      const user = authState.user
        ? authState.user
        : await Authentication.getInstance().GetUser()

      if (user && typeof user === 'object') {
        this.loadPerson(user.person)
        this.loadInvoice(user.invoice)
      } else {
        await this.initLoggedOut()
        if (this.email.length === 0) {
          this.email = authState.email
        }
      }
    } catch {
      await this.initLoggedOut()
      if (this.email.length === 0) {
        this.email = authState.email
      }
    }
  }

  private async initLoggedOut() {
    if (await Storage.hasInStoreAsync('form')) {
      const details = JSON.parse(await Storage.getFromStoreAsync('form'))
      this.loadPerson(details.person)
      this.loadInvoice(details.invoice)
    }
    if (this.email.length === 0) {
      this.email = authState.email
    }
  }

  private loadPerson(person?: Person) {
    if (!person) return

    this.email = person.email || ''
    this.emailConfirmation = person.email || ''
    this.name = person.name || ''
    this.phone = person.telephone ? person.telephone : ''
    this.zip = person.address.postalCode || ''
    this.city = person.address.addressLocality || ''
    this.address = person.address.streetAddress || ''
  }

  private loadInvoice(invoice?: Organization) {
    if (!invoice || this.invoiceIndividualsOnly) return

    this.invoiceName = invoice.name || ''
    this.invoiceZip = invoice.address.postalCode || ''
    this.invoiceCity = invoice.address.addressLocality || ''
    this.invoiceAddress = invoice.address.streetAddress || ''
    this.invoiceVat = invoice.vatID || ''

    this.isInvoiceDifferent = true
    this.cartState.isInvoiceDifferent = true
  }

  @computed get invoiceIndividualsOnly(): boolean {
    return this.cartState.content?.invoiceSettings?.individualsOnly || false
  }

  @computed get getPerson(): Person {
    return {
      '@id': '',
      '@type': 'Person',
      address: {
        '@type': 'Address',
        addressCountry: '',
        addressLocality: this.city,
        addressRegion: '',
        postalCode: this.zip,
        streetAddress: this.address,
        countryCode: this.selectedCountry?.alpha2 || 'hu',
      },
      email: this.email,
      name: this.name,
      telephone: this.phone,
    }
  }

  @computed get hasOptionalFields(): boolean {
    if (this.cartState.content?.fields) {
      return true
    }
    return false
  }

  @computed get getInvoice(): Organization | undefined {
    if (!this.isInvoiceDifferent || this.invoiceIndividualsOnly) {
      return undefined
    }
    return {
      '@type': 'Organization',
      address: {
        '@type': 'Address',
        addressCountry: '',
        addressLocality: this.invoiceCity,
        addressRegion: '',
        postalCode: this.invoiceZip,
        streetAddress: this.invoiceAddress,
        countryCode: this.selectedCountry?.alpha2 || 'hu',
      },
      image: '',
      logo: '',
      name: this.invoiceName,
      url: '',
      vatID: this.invoiceVat,
    }
  }

  public i18n(key: string): string {
    return this.Locale.translations[key]
  }

  @computed get checkboxFields(): CartField[] {
    return this.optionalFields.filter((field) => field.type === 'check')
  }

  private initializeCheckboxTracking() {
    this.checkboxFields.forEach((field) => {
      this.checkboxValues[field.id] =
        field.value === 'true' || Boolean(field.value)

      subscribe<string | boolean>(
        () => field.value,
        (newValue) => {
          this.checkboxValues[field.id] =
            newValue === 'true' || newValue === true
        }
      )
    })
  }

  private initializeCountries() {
    const locale = this.Locale.language as keyof typeof countries
    const list = Object.values(countries[locale])
    const collator = new Intl.Collator(locale)
    this.availableCountries = list.sort((a, b) =>
      collator.compare(a.name, b.name)
    )

    this.selectedCountry =
      this.availableCountries.find(
        ({ id }) => id === CartState.DEFAULT_COUNTRY_ID
      ) || CartState.DEFAULT_COUNTRY
  }

  private initializeMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          this.initializeEventListeners()
        }
      })
    })

    this.formEl = CartState.get().formElement

    if (this.formEl) {
      observer.observe(this.formEl, { childList: true, subtree: true })
    }
  }

  private initializeEventListeners() {
    if (!this.formEl) return
    const inputs = this.formEl.querySelectorAll('input')

    inputs.forEach((input) => {
      input.addEventListener('focus', this.removeErrorClass)
      input.addEventListener('input', this.removeErrorClass)
    })

    this.optionalFields.forEach((field) => {
      if (!this.formEl) return
      if (field.optional === false) {
        const inputId = `#field-${field.id}`
        if (field.type === 'text') {
          const input = this.formEl.querySelector(inputId) as HTMLInputElement
          input?.addEventListener('input', this.removeErrorClass)
        } else if (field.type === 'check') {
          const checkbox = this.formEl.querySelector(
            inputId
          ) as HTMLInputElement
          checkbox?.addEventListener('change', this.removeErrorClass)
        }
      }
    })
  }

  private removeErrorClass(event: Event) {
    const target = event.target as HTMLInputElement
    target.classList.remove('is-invalid')
    const inputId = target.id

    this.formEl = CartState.get().formElement
    if (!this.formEl) return
    const errorMessageElement = this.formEl.querySelector(
      `.error-message[data-for="${inputId}"]`
    ) as HTMLElement

    if (errorMessageElement) {
      errorMessageElement.classList.remove('visible')
    }
  }
}
